// Worklogs by person: the pure rules behind the grid, the summary strip and the person panel.
// Days are calendar days written "YYYY-MM-DD" (the app timezone, decided by the server); nothing here reads
// the browser's clock or timezone, so a day is the same day for everyone.

export interface WorklogRow {
  userId: string;
  accountId: string;
  userName: string;
  userRole?: string;
  managerName?: string | null;
  dailyMinutes: Record<string, number>;
  totalHours: number;
}

/** none: nothing logged; lo: under 5 h; ok: 5 to 8 h; hi: over 8 h (doubtful). */
export type Bucket = 'none' | 'lo' | 'ok' | 'hi';

export const LOW_HOURS = 5;
export const HIGH_HOURS = 8;

export function hoursBucket(hours: number): Bucket {
  if (hours <= 0) return 'none';
  if (hours < LOW_HOURS) return 'lo';
  return hours > HIGH_HOURS ? 'hi' : 'ok';
}

function parts(day: string): [number, number, number] {
  return [Number(day.slice(0, 4)), Number(day.slice(5, 7)), Number(day.slice(8, 10))];
}

/** Saturday or Sunday, read from the date written, not through any timezone. */
export function isWeekend(day: string): boolean {
  const [y, m, d] = parts(day);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return dow === 0 || dow === 6;
}

/** The weekdays that are over: every weekday of a past month, and in the open month those before today. */
export function workingDaysOver(days: string[], today: string): string[] {
  return days.filter((d) => d < today && !isWeekend(d));
}

const hoursOn = (row: WorklogRow, day: string) => (row.dailyMinutes?.[day] || 0) / 60;

export interface PersonStats {
  hours: number;
  loggedDays: number;
  /** Hours per day on the days the person logged anything; 0 when none. */
  averagePerLoggedDay: number;
  /** Weekdays that are over with nothing logged. May include leave and public holidays. */
  missingDays: string[];
  /** Weekdays that are over with something logged, but under the low mark. */
  underDays: number;
}

export function personStats(row: WorklogRow, days: string[], today: string): PersonStats {
  const logged = days.filter((d) => hoursOn(row, d) > 0);
  const over = workingDaysOver(days, today);
  const hours = row.totalHours || 0;
  return {
    hours,
    loggedDays: logged.length,
    averagePerLoggedDay: logged.length > 0 ? hours / logged.length : 0,
    missingDays: over.filter((d) => hoursOn(row, d) === 0),
    underDays: over.filter((d) => hoursBucket(hoursOn(row, d)) === 'lo').length,
  };
}

export interface MonthStats {
  hours: number;
  people: number;
  loggedDays: number;
  averagePerLoggedDay: number;
  missing: number;
  under: number;
  /** Cells per bucket over the people shown; `none` counts the weekdays that are over with nothing logged. */
  cells: Record<Bucket, number>;
}

export function monthStats(rows: WorklogRow[], days: string[], today: string): MonthStats {
  const over = new Set(workingDaysOver(days, today));
  const cells: Record<Bucket, number> = { none: 0, lo: 0, ok: 0, hi: 0 };
  let hours = 0;
  let loggedDays = 0;
  let under = 0;
  for (const r of rows) {
    hours += r.totalHours || 0;
    for (const d of days) {
      const b = hoursBucket(hoursOn(r, d));
      if (b === 'none') {
        if (over.has(d)) cells.none += 1;
      } else {
        cells[b] += 1;
        loggedDays += 1;
        if (b === 'lo' && over.has(d)) under += 1;
      }
    }
  }
  return { hours, people: rows.length, loggedDays, averagePerLoggedDay: loggedDays > 0 ? hours / loggedDays : 0, missing: cells.none, under, cells };
}

export type SortKey = 'user' | 'total' | 'average';
export interface Sort {
  key: SortKey;
  dir: 1 | -1;
}

/** Each press goes one step on: first the natural direction (A to Z, most hours first), then the other, then back to the page's own order. */
export function nextSort(current: Sort | null, key: SortKey): Sort | null {
  const first: 1 | -1 = key === 'user' ? 1 : -1;
  if (!current || current.key !== key) return { key, dir: first };
  if (current.dir === first) return { key, dir: (first * -1) as 1 | -1 };
  return null;
}

/** The people in the order asked for; with no sort the server's order (most recent worklog first) stays. */
export function sortRows<T extends WorklogRow>(rows: T[], sort: Sort | null): T[] {
  if (!sort) return rows;
  const loggedDays = (r: WorklogRow) => Object.values(r.dailyMinutes || {}).filter((m) => m > 0).length;
  const average = (r: WorklogRow) => (loggedDays(r) > 0 ? (r.totalHours || 0) / loggedDays(r) : 0);
  const by =
    sort.key === 'user'
      ? (a: T, b: T) => a.userName.localeCompare(b.userName)
      : sort.key === 'average'
        ? (a: T, b: T) => average(a) - average(b)
        : (a: T, b: T) => (a.totalHours || 0) - (b.totalHours || 0);
  return [...rows].sort((a, b) => sort.dir * by(a, b) || a.userName.localeCompare(b.userName));
}

export function matchesQuery(row: WorklogRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  return q === '' || row.userName.toLowerCase().includes(q);
}

// ─── The grid's frozen columns ───────────────────────────────────

export interface GridLayout {
  /** Column widths in px: the row number, the person, their role, their Jira manager, the total, and one day. */
  widths: { sr: number; user: number; role: number; manager: number; total: number; day: number };
  /** Where each frozen column sticks, in px from the left edge of the grid. */
  left: { sr: number; user: number; role: number; manager: number; total: number };
  /** The row number, role and manager columns. They are left out on a phone so the days have room. */
  showMeta: boolean;
}

// Wide screens pin five columns (number, name, role, manager, total). A phone grid is about 300 px wide, so pinning
// those would leave no room to see a day: there only the name and the total stay pinned, and the rest is left out.
export function gridLayout(phone: boolean, isAdmin: boolean): GridLayout {
  const widths = phone
    ? { sr: 0, user: 112, role: 0, manager: 0, total: 56, day: 52 }
    : { sr: 48, user: 192, role: 104, manager: 128, total: 96, day: 60 };
  const showMeta = !phone;
  const user = widths.sr;
  const role = user + widths.user;
  const manager = role + widths.role;
  const total = manager + (isAdmin && showMeta ? widths.manager : 0);
  return { widths, left: { sr: 0, user, role, manager, total }, showMeta };
}
